import React, { useContext, useMemo, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Flex, Box } from '@rebass/grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import { CardContent, Divider, Button, TextField } from '@material-ui/core';
import PageLayout from '../../page-layout';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import { useLocation, useParams } from '@reach/router';
import { useGymUsers } from '../../../lib/hooks/use-gym-users';
import UserListItem from '../../organisim/user-list-item';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import {
  getAvailablePermissions,
  getAssignedPermissions,
} from '../../../lib/fetchers/permissions';
import { updateUserPermissions } from '../../../lib/mutations/permissions';
import { AppContext } from '../../../app';
import { useUser } from '../../../lib/hooks/use-user';
import { colors } from '../../../config/theme';

const styles = {
  page: {
    padding: '16px',
  },
};

const UserPermissions = (props) => {
  const { classes } = props;
  const { gymUid: routeGymUid } = useParams();
  const location = useLocation();
  const { loggedInUserId } = useContext(AppContext);
  const { user } = useUser(loggedInUserId);
  const userGymUid = user?.gym?.uuid;
  const pathGymUidMatch = (location?.pathname || '').match(/gyms\/([^/]+)/);
  const pathGymUid = pathGymUidMatch ? pathGymUidMatch[1] : undefined;
  const gymUid = routeGymUid || userGymUid || pathGymUid;
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const { users = {}, loading } = useGymUsers(gymUid, searchTerm, {
    perPage: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const { data: assigned = [] } = useQuery(
    ['assigned-permissions', gymUid],
    () => getAssignedPermissions(gymUid),
    { enabled: !!gymUid, staleTime: 1000 * 30 }
  );

  const { data: availablePerms = [] } = useQuery(
    ['available-permissions', gymUid],
    () => getAvailablePermissions(gymUid),
    { enabled: !!gymUid, staleTime: 1000 * 60 * 10 }
  );

  const normalizedPerms = (availablePerms || [])
    .map((p) =>
      typeof p === 'string'
        ? p
        : p?.name || p?.key || p?.slug || String(p?.id || '')
    )
    .filter(Boolean);

  const permLabelToId = useMemo(() => {
    const map = new Map();
    (availablePerms || []).forEach((p) => {
      if (typeof p === 'string') {
        map.set(p, null);
      } else if (p && (p.name || p.id)) {
        const label = p.name || String(p.id);
        map.set(label, p.id != null ? p.id : null);
      }
    });
    return map;
  }, [availablePerms]);

  const [confirmPerm, setConfirmPerm] = useState({
    open: false,
    userId: null,
    name: '',
    perm: '',
    next: false,
  });
  const [toast, setToast] = useState(null);

  const mergedUsers = useMemo(() => {
    const userList = Array.isArray(users?.users) ? users.users : [];
    const assignedArr = Array.isArray(assigned) ? assigned : [];
    const assignedMap = new Map(
      assignedArr
        .map((ap) => [ap?.user?.id, ap])
        .filter(
          (pair) => typeof pair[0] === 'number' || typeof pair[0] === 'string'
        )
    );

    // Normalize assigned users to UI shape
    const normalizeAssigned = (ap) => ({
      id: ap?.user?.id,
      name: ap?.user?.name,
      email: ap?.user?.email,
      avatar_url: ap?.user?.avatar_url,
      uid: ap?.user?.uid,
      permissions: (ap.permissions || [])
        .map((p) =>
          typeof p === 'string' ? p : p?.name || String(p?.id || '')
        )
        .filter(Boolean),
    });

    // If no search term: show ONLY users with permissions
    if (!searchTerm || !searchTerm.trim()) {
      const assignedOnly = assignedArr.map(normalizeAssigned);
      return assignedOnly.slice().sort((a, b) => {
        const an = (a.name || '').toLowerCase();
        const bn = (b.name || '').toLowerCase();
        if (an < bn) return -1;
        if (an > bn) return 1;
        return 0;
      });
    }

    // With search: show search results EXCLUDING already-assigned users
    const merged = userList.map((u) => {
      const ap = assignedMap.get(u.id);
      if (ap && Array.isArray(ap.permissions)) {
        const apPerms = (ap.permissions || [])
          .map((p) =>
            typeof p === 'string' ? p : p?.name || String(p?.id || '')
          )
          .filter(Boolean);
        return { ...u, permissions: apPerms };
      }
      return u;
    });
    const filtered = merged.filter((u) => !assignedMap.has(u.id));
    return filtered.slice().sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });
  }, [users, assigned, searchTerm]);

  const { mutate: savePermissions } = useMutation(
    ({ userId, permissions }) =>
      updateUserPermissions({ gymUid, userId, permissions }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['gym-users', gymUid]);
        queryClient.invalidateQueries(['assigned-permissions', gymUid]);
        setToast({ type: 'success', message: 'Permissions updated.' });
      },
      onError: () =>
        setToast({ type: 'error', message: 'Failed to update permissions.' }),
    }
  );

  return (
    <PageLayout selected="User Permissions" pageTitle="User Permissions">
      <Box className={classes.page}>
        {!gymUid && (
          <Box mb={2}>
            <Typography color="textSecondary">
              Missing gym context. Open this from an Admin page or include a gym
              ID in the URL.
            </Typography>
          </Box>
        )}
        <Typography variant="h5">User Permissions</Typography>
        <Typography variant="body2" color="textSecondary">
          Search members and manage their permissions.
        </Typography>
        <Card style={{ marginTop: '16px' }}>
          <CardContent>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search members by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
            <Divider style={{ margin: '16px 0' }} />
            {/* Elevated Users section removed as requested */}
            {mergedUsers.map((member) => (
              <Flex
                key={member.id}
                alignItems="center"
                justifyContent="space-between"
                py={1}
              >
                <Box flex="1 1 auto" mr="8px">
                  <UserListItem user={member} />
                </Box>
                <Box mr="12px" display="flex" flexWrap="wrap">
                  {normalizedPerms.map((permValue) => {
                    const hasIt = (member.permissions || []).includes(
                      permValue
                    );
                    return (
                      <FormControlLabel
                        key={`${member.id}-${permValue}`}
                        control={
                          <Checkbox
                            checked={hasIt}
                            onChange={(e) => {
                              setConfirmPerm({
                                open: true,
                                userId: member.id,
                                name: member.name,
                                perm: permValue,
                                next: e.target.checked,
                              });
                            }}
                            color="primary"
                          />
                        }
                        label={permValue}
                      />
                    );
                  })}
                </Box>
              </Flex>
            ))}
            {users?.users?.length === 0 && !loading && (
              <Box py={2}>
                <Typography color="textSecondary" align="center">
                  No members found
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {toast && (
          <Box mt={2}>
            <Typography
              variant="body2"
              style={{
                color: toast.type === 'success' ? colors.success : colors.dangerStrong,
              }}
            >
              {toast.message}
            </Typography>
          </Box>
        )}

        <Dialog
          open={confirmPerm.open}
          onClose={() => setConfirmPerm({ open: false })}
        >
          <DialogTitle>Confirm Permission Change</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmPerm.next ? 'Grant' : 'Revoke'} "{confirmPerm.perm}" for{' '}
              {confirmPerm.name}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmPerm({ open: false })}
              color="default"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const { userId, perm, next } = confirmPerm;
                // Start from the merged list as rendered
                const currentLabels = new Set(
                  mergedUsers.find((u) => u.id === userId)?.permissions || []
                );
                const ap = (Array.isArray(assigned) ? assigned : []).find(
                  (a) => a?.user?.id === userId
                );
                const resolveId = (label) => {
                  const fromMap = permLabelToId.get(label);
                  if (fromMap !== undefined && fromMap !== null) return fromMap;
                  const found = ap?.permissions?.find((p) => {
                    const pname =
                      typeof p === 'string'
                        ? p
                        : p?.name || String(p?.id || '');
                    return pname === label;
                  });
                  return found && found.id != null ? found.id : null;
                };
                const currentIds = new Set(
                  Array.from(currentLabels)
                    .map((label) => resolveId(label))
                    .filter((id) => id !== null && id !== undefined)
                );
                const toggledId = resolveId(perm);
                if (toggledId !== null && toggledId !== undefined) {
                  if (next) currentIds.add(toggledId);
                  else currentIds.delete(toggledId);
                }
                savePermissions({
                  userId,
                  permissions: Array.from(currentIds),
                });
                setSearchTerm('');
                setConfirmPerm({ open: false });
              }}
              color="primary"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Elevated Users modal removed as requested */}
      </Box>
    </PageLayout>
  );
};

export default withStyles(styles)(UserPermissions);
